/**
 * iPad-style pre-synthesized audio generator
 * Creates Web Audio API synthesis for immediate UI feedback
 * Used as primary sound source while .ogg files load in background
 */

type AudioContext = ReturnType<typeof window.AudioContext>;

let cachedAudioContext: AudioContext | null = null;
const synthesizedSounds = new Map<string, () => void>();

function getAudioContext(): AudioContext {
  if (!cachedAudioContext && typeof window !== 'undefined' && window.AudioContext) {
    cachedAudioContext = new window.AudioContext();
  }
  return cachedAudioContext as AudioContext;
}

/**
 * Play a synthesized sound directly using Web Audio API
 * Much faster than converting to blob/data URL and using use-sound
 */
function playSynthesizedSound(frequencies: number[], durations: number[], amplitudes: number[] = []) {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    masterGain.connect(ctx.destination);
    masterGain.gain.setValueAtTime(0.4, now);
    masterGain.gain.linearRampToValueAtTime(0, now + 0.3);

    let currentTime = now;

    for (let i = 0; i < frequencies.length; i++) {
      const freq = frequencies[i];
      const duration = durations[i] || 0.1;
      const amp = amplitudes[i] || 0.4;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, currentTime);
      gain.gain.setValueAtTime(0, currentTime);
      gain.gain.linearRampToValueAtTime(amp, currentTime + 0.01);
      gain.gain.linearRampToValueAtTime(0, currentTime + duration);

      osc.connect(gain);
      gain.connect(masterGain);

      osc.start(currentTime);
      osc.stop(currentTime + duration);

      currentTime += duration;
    }
  } catch (err) {
    console.warn('[playSynthesizedSound] Error:', err);
  }
}

/**
 * Two-tone iPad beep - immediate playback
 * Frequencies: 523Hz + 659Hz (major third - very recognizable)
 */
export function playTwoTone(freq1: number = 523, freq2: number = 659, duration: number = 0.15) {
  playSynthesizedSound([freq1, freq2], [duration, 0], [0.4, 0.4]);
}

/**
 * Three-note tritone - immediate playback
 */
export function playTritone(freq1: number = 587, freq2: number = 659, freq3: number = 784, noteDuration: number = 0.08) {
  playSynthesizedSound([freq1, freq2, freq3], [noteDuration, noteDuration, noteDuration]);
}

/**
 * Bell/chime sound - immediate playback
 * Rich harmonic tone for success/alerts
 */
export function playChime(baseFreq: number = 880, duration: number = 0.25) {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    masterGain.connect(ctx.destination);
    masterGain.gain.setValueAtTime(0.3, now);
    masterGain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    // Multiple harmonics for bell-like sound
    const harmonics = [
      { freq: baseFreq, amp: 0.4 },
      { freq: baseFreq * 1.5, amp: 0.25 },
      { freq: baseFreq * 2, amp: 0.15 },
      { freq: baseFreq * 2.5, amp: 0.1 },
    ];

    for (const harmonic of harmonics) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.frequency.setValueAtTime(harmonic.freq, now);
      osc.type = 'sine';
      gain.gain.setValueAtTime(harmonic.amp, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

      osc.connect(gain);
      gain.connect(masterGain);

      osc.start(now);
      osc.stop(now + duration);
    }
  } catch (err) {
    console.warn('[playChime] Error:', err);
  }
}

/**
 * Descending glide - error/alert tone
 */
export function playErrorTone(startFreq: number = 659, endFreq: number = 440, duration: number = 0.2) {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(startFreq, now);
    osc.frequency.linearRampToValueAtTime(endFreq, now + duration);
    gain.gain.setValueAtTime(0.4, now);
    gain.gain.linearRampToValueAtTime(0, now + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + duration);
  } catch (err) {
    console.warn('[playErrorTone] Error:', err);
  }
}

/**
 * Rising glide - success/confirmation tone
 */
export function playSuccessTone(startFreq: number = 523, endFreq: number = 784, duration: number = 0.25) {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(startFreq, now);
    osc.frequency.linearRampToValueAtTime(endFreq, now + duration);
    gain.gain.setValueAtTime(0.35, now);
    gain.gain.linearRampToValueAtTime(0, now + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + duration);
  } catch (err) {
    console.warn('[playSuccessTone] Error:', err);
  }
}
